import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../../context/NavigationContext';
import CourseletSentencesView from './CourseletSentencesView';
import SchoolIcon from '@mui/icons-material/School';
import HomeIcon from '@mui/icons-material/Home';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const SummaryHeader = styled(Paper)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4, 3),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -20,
    right: -20,
    width: 100,
    height: 100,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    zIndex: 0,
  }
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(3, 0),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%',
      maxWidth: '300px'
    }
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
  minWidth: '200px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  }
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: 'white',
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.dark,
  }
}));

const CourseletSummary = ({ 
  courseletTitle = "this courselet",
  courseletId,
  courseId,
  sentenceCount = 0 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentCourse } = useNavigation();

  const handleStartNextCourselet = () => {
    // Refresh the course-learn page to start the next courselet
    navigate('/course-learn', { replace: true });
    window.location.reload();
  };

  const handleGoToLearningHome = () => {
    // Navigate to the language dashboard for the current course
    const targetCourseId = courseId || currentCourse?._id;
    if (targetCourseId) {
      navigate(`/language-dashboard/${targetCourseId}`);
    } else {
      navigate('/languages');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Completion Header */}
      <SummaryHeader elevation={0}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
            >
              <SchoolIcon sx={{ fontSize: 30, color: 'white' }} />
            </Box>
          </Box>
          
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2
            }}
          >
            🎉 Great job completing {courseletTitle}!
          </Typography>
          
          <Typography 
            variant="h6" 
            component="p"
            sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 400,
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            You've successfully learned {sentenceCount > 0 ? `${sentenceCount} new sentences` : 'new sentences'}! 
            Take a moment to review what you've accomplished.
          </Typography>
        </Box>
      </SummaryHeader>

      {/* Sentences View */}
      {courseletId ? (
        <Box sx={{ mb: 4 }}>
          <CourseletSentencesView 
            courseletId={courseletId}
            hideBackButton={true}
            showAsEmbedded={true}
          />
        </Box>
      ) : (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4, 
          color: 'text.secondary' 
        }}>
          <Typography variant="body1">
            Review your learning progress above!
          </Typography>
        </Box>
      )}

      {/* Action Buttons */}
      <Paper 
        elevation={0} 
        sx={{ 
          backgroundColor: alpha(theme.palette.grey[100], 0.5),
          borderRadius: theme.spacing(2),
          py: 2
        }}
      >
        <ButtonContainer>
          <PrimaryButton
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            onClick={handleStartNextCourselet}
          >
            Start Next Courselet
          </PrimaryButton>
          
          <SecondaryButton
            variant="outlined"
            size="large"
            startIcon={<HomeIcon />}
            onClick={handleGoToLearningHome}
          >
            Go to Learning Home
          </SecondaryButton>
        </ButtonContainer>
      </Paper>
    </Container>
  );
};

export default CourseletSummary;